import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export default function StripeCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const hasExecutedRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        
        // Prevent multiple executions
        if (hasExecutedRef.current) {
          return;
        }
        hasExecutedRef.current = true;

        // Check for OAuth errors
        if (error) {
          throw new Error(`Stripe OAuth error: ${error}`);
        }

        if (!code || !state) {
          throw new Error('Missing required OAuth parameters');
        }

        // Get current session
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error('Not authenticated');
        }

        // Send the OAuth callback to our backend
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-stripe-connect?action=oauth-callback`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to connect Stripe account');
        }

        const data = await response.json();
        
        setStatus('success');
        setMessage(`Successfully connected Stripe account: ${data.account_id}`);
        toast.success('Stripe account connected successfully!');

        // Redirect to partner dashboard after a short delay
        setTimeout(() => {
          navigate('/partner/payouts');
        }, 2000);

      } catch (error) {
        console.error('Stripe callback error:', error);
        setStatus('error');
        setMessage(error instanceof Error ? error.message : 'Failed to connect Stripe account');
        toast.error('Failed to connect Stripe account');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const getIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
        return <AlertCircle className="h-12 w-12 text-red-500" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (status) {
      case 'loading':
        return 'Connecting Your Stripe Account...';
      case 'success':
        return 'Connection Successful!';
      case 'error':
        return 'Connection Failed';
      default:
        return '';
    }
  };

  const getDescription = () => {
    switch (status) {
      case 'loading':
        return 'Please wait while we connect your Stripe account to your partner dashboard.';
      case 'success':
        return 'Your Stripe account has been successfully connected. You can now receive commission payouts.';
      case 'error':
        return message || 'There was an error connecting your Stripe account.';
      default:
        return '';
    }
  };

  return (
    <div className="container mx-auto py-12 px-4">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              {getIcon()}
            </div>
            <CardTitle>{getTitle()}</CardTitle>
            <CardDescription className="text-center">
              {getDescription()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {status === 'success' && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Redirecting to your partner dashboard...
                </p>
              </div>
            )}
            
            {status === 'error' && (
              <div className="text-center space-y-4">
                <Button 
                  onClick={() => navigate('/partner/payouts')}
                  className="w-full"
                >
                  Return to Partner Dashboard
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.reload()}
                  className="w-full"
                >
                  Try Again
                </Button>
              </div>
            )}

            {status === 'loading' && (
              <div className="text-center">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/partner/payouts')}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}